import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Collapse
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import MaterialAssignmentDialog from '../components/MaterialAssignmentDialog';
import SaddleAssignmentDialog from './SaddleAssignmentDialog';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RebarCartAssignmentDialog from './RebarCartAssignmentDialog';
import { getConfig } from '../config';

//const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const { API_BASE_URL } = getConfig();

interface WaitingSale {
  id: number;
  item: string;
  amount: number;
  state: string;
  created_at: string;
  contract_date: string;
  company_name: string;
  site_name: string;
  material_type?: 'new' | 'stock' | null;
  purpose?: string;
}

interface MaterialAssignment {
  crown?: { height: number; radius: number; quantity: number; type: 'new' | 'reuse' };
  left_wing?: { width: number; radius: number; quantity: number; type: 'new' | 'reuse' };
  right_wing?: { width: number; radius: number; quantity: number; type: 'new' | 'reuse' };
  column?: { height: number; thickness: string; quantity: number; type: 'new' | 'reuse' };
  upper?: { width: number; thickness: string; quantity: number; type: 'new' | 'reuse' };
  lower_beam?: { length: number; thickness: string; quantity: number; type: 'new' | 'reuse' };
  upper_beam?: { length: number; thickness: string; quantity: number; type: 'new' | 'reuse' };
  reuseMaterials?: Record<string, Array<{ id: number; quantity: number }>>;
  saddle?: SaddleInput;
}

interface SaddleInput {
  upper_radius: number;
  lower_radius: number;
  width: number;
  height: number;
}


export default function MaterialManagement() {
  const [waitingSales, setWaitingSales] = useState<WaitingSale[]>([]);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<WaitingSale | null>(null);
  const [materialAssignment, setMaterialAssignment] = useState<MaterialAssignment>({});
  const [localInputs, setLocalInputs] = useState<MaterialAssignment>({});
  const localInputsRef = useRef<MaterialAssignment>({});
  const [saddleDialogOpen, setSaddleDialogOpen] = useState(false);
  const [assignedMap, setAssignedMap] = useState<Record<number, MaterialAssignment>>({});
  const [rebarDialogOpen, setRebarDialogOpen] = useState(false);


  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // useEffect(() => {
  //   fetchWaitingSales();
  // }, []);

  useEffect(() => {
    const fetchAssignedMaterials = async () => {
      try {
        const { data } = await api.get('/api/material-assignments');
        const map: Record<number, MaterialAssignment> = {};
        data.forEach((entry: any) => {
          map[entry.sale_item_id] = entry.materials;
        });
        setAssignedMap(map);
      } catch (e) {
        enqueueSnackbar('자재 할당 정보를 불러오지 못했습니다.', { variant: 'error' });
      }
    };

    fetchAssignedMaterials();  // ✅ 여기가 핵심
    fetchWaitingSales();       // 기존처럼 대기중 매출도 같이 불러오기
  }, []);

  const fetchWaitingSales = async () => {
    try {
      const response = await api.get('/api/sales/waiting');
      setWaitingSales(response.data);
    } catch (error) {
      console.error('대기 중인 매출 목록 조회 오류:', error);
      enqueueSnackbar('대기 중인 매출 목록을 불러오는데 실패했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };


  const handleMaterialTypeChange = async (saleId: number, type: 'new' | 'stock') => {
    try {
      await api.put(`/api/sales/${saleId}/material-type`, { material_type: type });

      setWaitingSales(prevSales =>
        prevSales.map(sale =>
          sale.id === saleId
            ? { ...sale, material_type: type }
            : sale
        )
      );

      enqueueSnackbar('자재 타입이 업데이트되었습니다.', { variant: 'success' });
    } catch (error) {
      console.error('자재 타입 업데이트 오류:', error);
      enqueueSnackbar('자재 타입 업데이트에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/sales/${id}`);
      fetchWaitingSales();
    } catch (error) {
      console.error('매출 삭제 중 오류 발생:', error);
      alert('매출 삭제에 실패했습니다.');
    }
  };

  const formatMaterialDetails = (material: any, type: string) => {
    const parts: string[] = [];

    if (type === 'crown') {
      parts.push(`높이: ${material.height}mm`);
      parts.push(`반지름: ${material.radius}mm`);
    } else if (type === 'left_wing' || type === 'right_wing') {
      parts.push(`너비: ${material.width}mm`);
      parts.push(`반지름: ${material.radius}mm`);
    } else if (type === 'column') {
      parts.push(`높이: ${material.height}mm`);
      parts.push(`두께: ${material.thickness}mm`);
    } else if (type === 'upper') {
      parts.push(`너비: ${material.width}mm`);
      parts.push(`두께: ${material.thickness}mm`);
    } else if (type === 'lower_beam' || type === 'upper_beam') {
      parts.push(`길이: ${material.length}mm`);
      parts.push(`두께: ${material.thickness}mm`);
    } else if (type === 'saddle') {
      parts.push(`상부 R: ${material.upper_radius}mm`);
      parts.push(`하부 R: ${material.lower_radius}mm`);
      parts.push(`너비: ${material.width}mm`);
      parts.push(`높이: ${material.height}mm`);
    }

    if (material.quantity !== undefined) {
      parts.push(`수량: ${material.quantity}개`);
    }

    if (material.type !== undefined) {
      parts.push(`타입: ${material.type === 'new' ? '신규' : '재활용'}`);
    }

    return parts.join(' ');
  };

  const getMaterialLabel = (type: string) => {
    switch (type) {
      case 'crown': return '크라운';
      case 'left_wing': return '왼쪽 날개';
      case 'right_wing': return '오른쪽 날개';
      case 'column': return '기둥';
      case 'upper': return '상부';
      case 'lower_beam': return '하부 종빔';
      case 'upper_beam': return '상부 종빔';
      case 'saddle': return '새들';
      default: return type;
    }
  };

  const handleAssignmentClick = useCallback((sale: WaitingSale) => {
    setSelectedSale(sale);
    if (sale.purpose === '라이닝폼') {
      setAssignmentDialogOpen(true);
    } else if (sale.purpose === '새들') {
      setSaddleDialogOpen(true); // ✅ 새들 전용 다이얼로그
    } else if (sale.purpose === '철근대차') {
      setRebarDialogOpen(true);
    }
  }, []);

  const handleAssignmentClose = useCallback(() => {
    setAssignmentDialogOpen(false);
    setSaddleDialogOpen(false);
    setSelectedSale(null);
    setRebarDialogOpen(false);
  }, []);

  const handleLocalInputChange = useCallback((material: string, field: string, value: string | 'new' | 'reuse') => {
    const newValue = field === 'type' ? value : Number(value);
    localInputsRef.current = {
      ...localInputsRef.current,
      [material]: {
        ...localInputsRef.current[material as keyof MaterialAssignment],
        [field]: newValue
      }
    };
    setLocalInputs(localInputsRef.current);
  }, []);

  const handleAssignmentSubmit = useCallback(async (data: MaterialAssignment) => {
    if (!selectedSale) return;

    try {
      // 데이터 변환 및 검증
      if (selectedSale.purpose === '라이닝폼') {
        const transformedData = {
          crown: {
            height: data.crown?.height ? Number(data.crown.height) : null,
            radius: data.crown?.radius ? Number(data.crown.radius) : null,
            quantity: data.crown?.quantity ? Number(data.crown.quantity) : null,
            type: data.crown?.type || 'new'
          },
          left_wing: {
            width: data.left_wing?.width ? Number(data.left_wing.width) : null,
            radius: data.left_wing?.radius ? Number(data.left_wing.radius) : null,
            quantity: data.left_wing?.quantity ? Number(data.left_wing.quantity) : null,
            type: data.left_wing?.type || 'new'
          },
          right_wing: {
            width: data.right_wing?.width ? Number(data.right_wing.width) : null,
            radius: data.right_wing?.radius ? Number(data.right_wing.radius) : null,
            quantity: data.right_wing?.quantity ? Number(data.right_wing.quantity) : null,
            type: data.right_wing?.type || 'new'
          },
          column: {
            height: data.column?.height ? Number(data.column.height) : null,
            thickness: data.column?.thickness || '',
            quantity: data.column?.quantity ? Number(data.column.quantity) : null,
            type: data.column?.type || 'new'
          },
          upper: {
            width: data.upper?.width ? Number(data.upper.width) : null,
            thickness: data.upper?.thickness || '',
            quantity: data.upper?.quantity ? Number(data.upper.quantity) : null,
            type: data.upper?.type || 'new'
          },
          upper_beam: {
            length: data.upper_beam?.length ? Number(data.upper_beam.length) : null,
            thickness: data.upper_beam?.thickness || '',
            quantity: data.upper_beam?.quantity ? Number(data.upper_beam.quantity) : null,
            type: data.upper_beam?.type || 'new'
          },
          lower_beam: {
            length: data.lower_beam?.length ? Number(data.lower_beam.length) : null,
            thickness: data.lower_beam?.thickness || '',
            quantity: data.lower_beam?.quantity ? Number(data.lower_beam.quantity) : null,
            type: data.lower_beam?.type || 'new'
          }

        };

        console.log('전송 데이터:', transformedData);

        await api.post(`/api/sales/${selectedSale.id}/materials`, transformedData);
      } else if (selectedSale.purpose === '새들') {
        const saddle = {
          upper_radius: data.saddle?.upper_radius ?? null,
          lower_radius: data.saddle?.lower_radius ?? null,
          width: data.saddle?.width ?? null,
          height: data.saddle?.height ?? null
        };
        await api.post(`/api/sale-items/${selectedSale.id}/saddles`, saddle);
      }
      enqueueSnackbar('자재가 할당되었습니다.', { variant: 'success' });
      handleAssignmentClose();
    } catch (error) {
      console.error('자재 할당 오류:', error);
      enqueueSnackbar('자재 할당에 실패했습니다.', { variant: 'error' });
    }
  }, [selectedSale, enqueueSnackbar]);

  const AssignmentDialog = useMemo(() => {
    const DialogComponent = () => (
      <Dialog open={assignmentDialogOpen} onClose={handleAssignmentClose} maxWidth="md" fullWidth>
        <DialogTitle>자재 할당</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {selectedSale?.purpose === '라이닝폼' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">크라운</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth>
                        <InputLabel>타입</InputLabel>
                        <Select
                          value={localInputs.crown?.type || 'new'}
                          onChange={(e) => handleLocalInputChange('crown', 'type', e.target.value as 'new' | 'reuse')}
                          label="타입"
                        >
                          <MenuItem value="new">신규</MenuItem>
                          <MenuItem value="reuse">재활용</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="높이"
                        type="number"
                        value={localInputs.crown?.height || ''}
                        onChange={(e) => handleLocalInputChange('crown', 'height', e.target.value)}
                        inputProps={{ step: '0.1' }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="반지름"
                        type="number"
                        value={localInputs.crown?.radius || ''}
                        onChange={(e) => handleLocalInputChange('crown', 'radius', e.target.value)}
                        inputProps={{ step: '0.1' }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="수량"
                        type="number"
                        value={localInputs.crown?.quantity || ''}
                        onChange={(e) => handleLocalInputChange('crown', 'quantity', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* 🔹 왼쪽 날개 */}
                <Grid item xs={12}>
                  <Typography variant="h6">왼쪽 날개</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth>
                        <InputLabel>타입</InputLabel>
                        <Select
                          value={localInputs.left_wing?.type || 'new'}
                          onChange={(e) => handleLocalInputChange('left_wing', 'type', e.target.value)}
                        >
                          <MenuItem value="new">신규</MenuItem>
                          <MenuItem value="reuse">재활용</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="너비" value={localInputs.left_wing?.width || ''} onChange={(e) => handleLocalInputChange('left_wing', 'width', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="반지름" value={localInputs.left_wing?.radius || ''} onChange={(e) => handleLocalInputChange('left_wing', 'radius', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="수량" value={localInputs.left_wing?.quantity || ''} onChange={(e) => handleLocalInputChange('left_wing', 'quantity', e.target.value)} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* 🔹 오른쪽 날개 */}
                <Grid item xs={12}>
                  <Typography variant="h6">오른쪽 날개</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth>
                        <InputLabel>타입</InputLabel>
                        <Select
                          value={localInputs.right_wing?.type || 'new'}
                          onChange={(e) => handleLocalInputChange('right_wing', 'type', e.target.value)}
                        >
                          <MenuItem value="new">신규</MenuItem>
                          <MenuItem value="reuse">재활용</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="너비" value={localInputs.right_wing?.width || ''} onChange={(e) => handleLocalInputChange('right_wing', 'width', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="반지름" value={localInputs.right_wing?.radius || ''} onChange={(e) => handleLocalInputChange('right_wing', 'radius', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="수량" value={localInputs.right_wing?.quantity || ''} onChange={(e) => handleLocalInputChange('right_wing', 'quantity', e.target.value)} />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">대차기둥</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth>
                        <InputLabel>타입</InputLabel>
                        <Select
                          value={localInputs.column?.type || 'new'}
                          onChange={(e) => handleLocalInputChange('column', 'type', e.target.value as 'new' | 'reuse')}
                          label="타입"
                        >
                          <MenuItem value="new">신규</MenuItem>
                          <MenuItem value="reuse">재활용</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="높이"
                        type="number"
                        value={localInputs.column?.height || ''}
                        onChange={(e) => handleLocalInputChange('column', 'height', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="두께"
                        type="number"
                        value={localInputs.column?.thickness || ''}
                        onChange={(e) => handleLocalInputChange('column', 'thickness', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="수량"
                        type="number"
                        value={localInputs.column?.quantity || ''}
                        onChange={(e) => handleLocalInputChange('column', 'quantity', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6">대차상부</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth>
                        <InputLabel>타입</InputLabel>
                        <Select
                          value={localInputs.upper?.type || 'new'}
                          onChange={(e) => handleLocalInputChange('upper', 'type', e.target.value as 'new' | 'reuse')}
                          label="타입"
                        >
                          <MenuItem value="new">신규</MenuItem>
                          <MenuItem value="reuse">재활용</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="너비"
                        type="number"
                        value={localInputs.upper?.width || ''}
                        onChange={(e) => handleLocalInputChange('upper', 'width', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="두께"
                        type="number"
                        value={localInputs.upper?.thickness || ''}
                        onChange={(e) => handleLocalInputChange('upper', 'thickness', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="수량"
                        type="number"
                        value={localInputs.upper?.quantity || ''}
                        onChange={(e) => handleLocalInputChange('upper', 'quantity', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {/* 🔹 하부 종빔 */}
                <Grid item xs={12}>
                  <Typography variant="h6">하부 종빔</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth>
                        <InputLabel>타입</InputLabel>
                        <Select
                          value={localInputs.lower_beam?.type || 'new'}
                          onChange={(e) => handleLocalInputChange('lower_beam', 'type', e.target.value)}
                        >
                          <MenuItem value="new">신규</MenuItem>
                          <MenuItem value="reuse">재활용</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="길이" value={localInputs.lower_beam?.length || ''} onChange={(e) => handleLocalInputChange('lower_beam', 'length', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="두께" value={localInputs.lower_beam?.thickness || ''} onChange={(e) => handleLocalInputChange('lower_beam', 'thickness', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="수량" value={localInputs.lower_beam?.quantity || ''} onChange={(e) => handleLocalInputChange('lower_beam', 'quantity', e.target.value)} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* 🔹 상부 종빔 */}
                <Grid item xs={12}>
                  <Typography variant="h6">상부 종빔</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth>
                        <InputLabel>타입</InputLabel>
                        <Select
                          value={localInputs.upper_beam?.type || 'new'}
                          onChange={(e) => handleLocalInputChange('upper_beam', 'type', e.target.value)}
                        >
                          <MenuItem value="new">신규</MenuItem>
                          <MenuItem value="reuse">재활용</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="길이" value={localInputs.upper_beam?.length || ''} onChange={(e) => handleLocalInputChange('upper_beam', 'length', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="두께" value={localInputs.upper_beam?.thickness || ''} onChange={(e) => handleLocalInputChange('upper_beam', 'thickness', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="수량" value={localInputs.upper_beam?.quantity || ''} onChange={(e) => handleLocalInputChange('upper_beam', 'quantity', e.target.value)} />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
            {selectedSale?.purpose === '새들' && (
              <Grid item xs={12}>
                <Typography variant="h6">새들 정보</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="상부 R값"
                      type="number"
                      value={localInputs.saddle?.upper_radius || ''}
                      onChange={(e) => handleLocalInputChange('saddle', 'upper_radius', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="하부 R값"
                      type="number"
                      value={localInputs.saddle?.lower_radius || ''}
                      onChange={(e) => handleLocalInputChange('saddle', 'lower_radius', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="너비"
                      type="number"
                      value={localInputs.saddle?.width || ''}
                      onChange={(e) => handleLocalInputChange('saddle', 'width', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="높이"
                      type="number"
                      value={localInputs.saddle?.height || ''}
                      onChange={(e) => handleLocalInputChange('saddle', 'height', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignmentClose}>취소</Button>
          <Button onClick={() => handleAssignmentSubmit(localInputsRef.current)} variant="contained" color="primary">
            할당
          </Button>
        </DialogActions>
      </Dialog>
    );
    return DialogComponent;
  }, [assignmentDialogOpen, selectedSale, localInputs, handleLocalInputChange, handleAssignmentSubmit]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          자재 할당 리스트
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell /> {/* 펼치기 버튼용 칼럼 */}
              <TableCell>업체</TableCell>
              <TableCell>현장</TableCell>
              <TableCell>항목</TableCell>
              <TableCell>계약금액</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>자재 용도</TableCell>
              <TableCell>자재 할당</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(waitingSales) && waitingSales.length > 0 ? (
              waitingSales.map((sale) => (
                <React.Fragment key={sale.id}>
                  <TableRow>
                    <TableCell>
                      {sale.state === '대기' && assignedMap[sale.id] && (
                        <IconButton onClick={() => setExpandedRow(prev => prev === sale.id ? null : sale.id)}>
                          {expandedRow === sale.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>{sale.company_name}</TableCell>
                    <TableCell>{sale.site_name}</TableCell>
                    <TableCell>{sale.item}</TableCell>
                    <TableCell>{sale.amount.toLocaleString()}천원</TableCell>
                    <TableCell>
                      <Chip
                        label={sale.state}
                        color={sale.state === '대기' ? 'default' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>{sale.purpose || '-'}</TableCell>
                    <TableCell>
                      {(sale.purpose === '라이닝폼' || sale.purpose === '새들' || sale.purpose === '철근대차') && (
                        <Button
                          variant="outlined"
                          startIcon={<AssignmentIcon />}
                          onClick={() => handleAssignmentClick(sale)}
                        >
                          할당
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* 하위 자재 정보 Row */}
                  {sale.state === '대기' && assignedMap[sale.id] && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Collapse in={expandedRow === sale.id} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>자재 목록</Typography>
                            <ul>
                              {Object.entries(assignedMap[sale.id]).map(([key, value]: any) => (
                                <li key={key}>
                                  <strong>{getMaterialLabel(key)}</strong> – {formatMaterialDetails(value, key)}
                                </li>
                              ))}
                            </ul>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  등록된 매출이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <MaterialAssignmentDialog
        open={assignmentDialogOpen}
        onClose={handleAssignmentClose}
        onSubmit={handleAssignmentSubmit}
        selectedSale={selectedSale}
      />
      <SaddleAssignmentDialog
        open={saddleDialogOpen}
        onClose={handleAssignmentClose}
        onAssignSuccess={() => {
          enqueueSnackbar('새들 자재가 성공적으로 할당되었습니다.', { variant: 'success' });
          fetchWaitingSales();
          handleAssignmentClose();
        }}
        saleItemId={selectedSale?.id ?? 0}
      />
      <RebarCartAssignmentDialog
        open={rebarDialogOpen}
        onClose={handleAssignmentClose}
        onAssignSuccess={() => {
          enqueueSnackbar('철근대차 자재가 성공적으로 할당되었습니다.', { variant: 'success' });
          fetchWaitingSales();
          handleAssignmentClose();
        }}
        saleItemId={selectedSale?.id ?? 0}
      />
    </Box>
  );
} 